import { useEffect } from "react";
import type { FormatDateOptions } from "react-intl";
import { useIntl } from "react-intl";
import { getTimeUnitId } from "@akina/utils";
import { useIntlConfigurationStoreCare, useIntlConfigurationStorePortal } from "../../infra/locales/constants";
import {
  type FormatTimeOptions,
  type FormatMessageArgs,
  type CopyKeyPortal,
  type CopyKeyCare,
} from "../../infra/locales/types";
import { useLocaleCopyStore } from "./";
import { useCmsCopyStore } from "./useCmsCopyStore";

export const useInitializeIntlShared = <TranslationKey extends string>() => {
  const { formatMessage, formatDate, formatNumber, formatRelativeTime } = useIntl();

  const formatTimeWithUnits = ({
    seconds = 0,
    minutes = 0,
    hours = 0,
    hideSeconds = false,
  }: FormatTimeOptions & { hideSeconds?: boolean; hideColumn?: boolean }) => {
    const timeValue = formatTime({
      seconds,
      minutes,
      hours,
      hideSeconds,
    });

    const effectiveSeconds = hideSeconds ? 0 : seconds;
    const effectiveMinutes = minutes || (hideSeconds && seconds >= 30 ? 1 : 0);

    const unitId = getTimeUnitId({
      seconds: effectiveSeconds,
      minutes: effectiveMinutes,
      hours,
    }) as TranslationKey;

    return `${timeValue}${" "}${formatMessage({ id: unitId })}`;
  };

  const typedFormatMessage = (
    descriptor: FormatMessageArgs[0] & {
      id?: TranslationKey;
    },
    values?: FormatMessageArgs[1],
    options?: FormatMessageArgs[2],
  ) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formatMessage(descriptor, values as any, options);

  const formatTime = ({
    seconds = 0,
    minutes = 0,
    hours = 0,
    alwaysShowMinutes = false,
    alwaysShowHours = false,
    hideSeconds = false,
  }: FormatTimeOptions & { hideSeconds?: boolean }) => {
    const options: FormatDateOptions = {
      hourCycle: "h23",
    };

    const totalTimeInSeconds = seconds + minutes * 60 + hours * 3600;

    if (!hideSeconds) {
      options.second = "numeric";
    }

    if (totalTimeInSeconds >= 60 || alwaysShowMinutes) {
      options.minute = "numeric";
    }

    if (totalTimeInSeconds >= 3600 || alwaysShowHours) {
      options.hour = "numeric";
    }

    return formatDate(new Date(0, 0, 0, hours ?? 0, minutes ?? 0, seconds ?? 0), options);
  };

  const customFormatDate = (date?: Date | string, options?: FormatDateOptions) => {
    return formatDate(date, options).replaceAll("/", ".");
  }

  const formatPercentage = (val: number) =>
    formatNumber(val, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      style: "percent",
    });

  return {
    formatMessage: typedFormatMessage,
    formatDate: customFormatDate,
    formatNumber,
    formatRelativeTime,
    formatTime,
    formatPercentage,
    formatTimeWithUnits,
  };
};

const useHandleLocaleUpdates = <T extends CopyKeyCare | CopyKeyPortal>({
  setIntl,
  intlValues,
}: {
  setIntl: (intl: ReturnType<typeof useInitializeIntlShared<T>>) => void;
  intlValues: ReturnType<typeof useInitializeIntlShared<T>>;
}) => {
  const localeCopy = useLocaleCopyStore(state => state.localeCopy);
  const cmsCopy = useCmsCopyStore(state => state.cmsCopy);
  const isLoadingLocaleCopy = useLocaleCopyStore(state => state.isLoadingLocaleCopy);

  useEffect(() => {
    setIntl(intlValues);
  }, [localeCopy, setIntl, intlValues]);

  const isCopyInitializing = isLoadingLocaleCopy || !cmsCopy?.otherStories || !localeCopy;
  return { isLocaleDataLoading: isCopyInitializing };
};

const useInitializeIntlCare = () => {
  const intlValues = useInitializeIntlShared<CopyKeyCare>();
  const { setIntl } = useIntlConfigurationStoreCare();
  const { isLocaleDataLoading } = useHandleLocaleUpdates({
    setIntl,
    intlValues,
  });
  return { isLocaleDataLoading };
};

const useInitializeIntlPortal = () => {
  const intlValues = useInitializeIntlShared<CopyKeyPortal>();
  const { setIntl } = useIntlConfigurationStorePortal();
  const { isLocaleDataLoading } = useHandleLocaleUpdates({
    setIntl,
    intlValues,
  });
  return { isLocaleDataLoading };
};

export const getInitializeIntl = {
  care: useInitializeIntlCare,
  portal: useInitializeIntlPortal,
};
